import type { CompanionStyle } from '@/config/companions'
import { normalizeCompanionStyle } from '@/config/companions'
import { getAnswer } from '@/utils/onboardingStorage'
import { useTranslation } from 'react-i18next'

type NounKey = 'girlfriend' | 'boyfriend' | 'friend' | 'companion'

function getCompanionStyleAnswer(): CompanionStyle {
  return normalizeCompanionStyle(getAnswer('companion-style')?.answer_key)
}

function getCompanionNounKey(): NounKey {
  const style = getCompanionStyleAnswer()
  switch (style) {
    case 'girlfriend':
      return 'girlfriend'
    case 'boyfriend':
      return 'boyfriend'
    case 'friend':
      return 'friend'
    case 'open':
      return 'companion'
    default: {
      const exhaustive: never = style
      throw new Error(`Unhandled companion style: ${exhaustive}`)
    }
  }
}

export function isRomanticStyle(): boolean {
  const style = getCompanionStyleAnswer()
  return style === 'girlfriend' || style === 'boyfriend'
}

export function getGoalKey(): string {
  return getAnswer('goal')?.answer_key || 'friend'
}

/**
 * Returns the flirty `_romantic` variant of a translation key when the user
 * chose a girlfriend/boyfriend, otherwise the base (platonic) key.
 * The `_romantic` variant must exist in translation.json.
 */
export function toneVariant(baseKey: string): string {
  return isRomanticStyle() ? `${baseKey}_romantic` : baseKey
}

/** Localized noun for the chosen companion style, e.g. "girlfriend" / "best friend". */
export function useCompanionNoun(): string {
  const { t } = useTranslation()
  return t(`personalization.${getCompanionNounKey()}`)
}
